import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Mail, Plus, Edit2, Trash2, X, Loader2, Save, Eye, Copy, 
  Type, Image, Link2, Minus, GripVertical, ChevronUp, ChevronDown,
  Palette, LayoutTemplate, Sparkles, Send, ArrowLeft, Smartphone, Monitor
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useBranding } from '../../contexts/BrandingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pre-designed Marketing Email Templates
const EMAIL_TEMPLATE_LIBRARY = [
  {
    id: 'promo-flash-sale',
    name: 'Flash Sale',
    category: 'promotional',
    thumbnail: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
    description: 'Urgency-driven flash sale announcement',
    blocks: [
      { type: 'header', content: { text: 'FLASH SALE', align: 'center', fontSize: '32px', color: '#FF6B6B', backgroundColor: '#FEE2E2' }},
      { type: 'text', content: { text: 'Limited Time Only - 24 Hours!', align: 'center', fontSize: '18px', color: '#991B1B' }},
      { type: 'image', content: { url: '', alt: 'Sale Image', align: 'center' }},
      { type: 'text', content: { text: 'Get {discount_value} off all treatments and products. Use code at checkout or mention in-store.', align: 'center', fontSize: '16px' }},
      { type: 'button', content: { text: 'Shop Now', url: '{spa_website}', align: 'center', backgroundColor: '#EF4444', textColor: '#ffffff' }},
      { type: 'divider', content: {}},
      { type: 'text', content: { text: 'Offer expires {expiration_date}. Cannot be combined with other offers.', align: 'center', fontSize: '12px', color: '#6B7280' }}
    ]
  },
  {
    id: 'promo-new-treatment',
    name: 'New Treatment Launch',
    category: 'promotional',
    thumbnail: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
    description: 'Announce a new treatment or service',
    blocks: [
      { type: 'header', content: { text: 'Introducing: {treatment_name}', align: 'center', fontSize: '28px', color: '#5B21B6' }},
      { type: 'image', content: { url: '', alt: 'Treatment Image', align: 'center' }},
      { type: 'text', content: { text: 'We\'re excited to announce our newest treatment! Experience the latest in aesthetic innovation.', align: 'center', fontSize: '16px' }},
      { type: 'text', content: { text: '{treatment_description}', align: 'left', fontSize: '14px' }},
      { type: 'button', content: { text: 'Book Your Session', url: '{booking_link}', align: 'center', backgroundColor: '#8B5CF6', textColor: '#ffffff' }},
      { type: 'text', content: { text: 'Questions? Reply to this email or call us at {spa_phone}.', align: 'center', fontSize: '14px', color: '#6B7280' }}
    ]
  },
  {
    id: 'newsletter-monthly',
    name: 'Monthly Newsletter',
    category: 'newsletter',
    thumbnail: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
    description: 'Monthly updates and tips',
    blocks: [
      { type: 'header', content: { text: '{spa_name} Monthly Update', align: 'center', fontSize: '28px', color: '#065F46' }},
      { type: 'text', content: { text: 'Hi {client_name},', align: 'left', fontSize: '16px' }},
      { type: 'text', content: { text: 'Here\'s what\'s new this month at {spa_name}! We have exciting updates, tips, and exclusive offers just for you.', align: 'left', fontSize: '14px' }},
      { type: 'divider', content: {}},
      { type: 'header', content: { text: 'This Month\'s Highlight', align: 'left', fontSize: '20px', color: '#059669' }},
      { type: 'image', content: { url: '', alt: 'Featured Image', align: 'center' }},
      { type: 'text', content: { text: 'Add your monthly highlight content here...', align: 'left', fontSize: '14px' }},
      { type: 'divider', content: {}},
      { type: 'header', content: { text: 'Skincare Tip of the Month', align: 'left', fontSize: '20px', color: '#059669' }},
      { type: 'text', content: { text: 'Add your skincare tip here...', align: 'left', fontSize: '14px' }},
      { type: 'button', content: { text: 'Book Your Next Visit', url: '{booking_link}', align: 'center', backgroundColor: '#10B981', textColor: '#ffffff' }}
    ]
  },
  {
    id: 'promo-seasonal',
    name: 'Seasonal Promotion',
    category: 'promotional',
    thumbnail: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
    description: 'Seasonal offers and specials',
    blocks: [
      { type: 'header', content: { text: 'Spring Into Beauty', align: 'center', fontSize: '32px', color: '#92400E', backgroundColor: '#FEF3C7' }},
      { type: 'text', content: { text: 'Refresh your look this season with our exclusive spring specials!', align: 'center', fontSize: '18px' }},
      { type: 'image', content: { url: '', alt: 'Seasonal Image', align: 'center' }},
      { type: 'text', content: { text: 'Enjoy {discount_value} off select treatments this month only.', align: 'center', fontSize: '16px' }},
      { type: 'button', content: { text: 'View Specials', url: '{spa_website}/specials', align: 'center', backgroundColor: '#F59E0B', textColor: '#ffffff' }}
    ]
  },
  {
    id: 'referral-invite',
    name: 'Referral Invitation',
    category: 'referral',
    thumbnail: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
    description: 'Encourage referrals with rewards',
    blocks: [
      { type: 'header', content: { text: 'Share the Love', align: 'center', fontSize: '28px', color: '#9D174D' }},
      { type: 'text', content: { text: 'Hi {client_name},', align: 'left', fontSize: '16px' }},
      { type: 'text', content: { text: 'Love your results? Share the gift of beauty with friends and family!', align: 'center', fontSize: '16px' }},
      { type: 'text', content: { text: 'For every friend you refer, you\'ll receive {referral_reward} and they\'ll get {friend_discount} off their first visit.', align: 'center', fontSize: '14px' }},
      { type: 'button', content: { text: 'Get Your Referral Link', url: '{referral_link}', align: 'center', backgroundColor: '#EC4899', textColor: '#ffffff' }},
      { type: 'text', content: { text: 'Your unique referral code: {referral_code}', align: 'center', fontSize: '16px', color: '#9D174D' }}
    ]
  },
  {
    id: 'reengagement',
    name: 'We Miss You',
    category: 'retention',
    thumbnail: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
    description: 'Re-engage inactive clients',
    blocks: [
      { type: 'header', content: { text: 'We Miss You, {client_name}!', align: 'center', fontSize: '28px', color: '#1E40AF' }},
      { type: 'text', content: { text: 'It\'s been a while since your last visit to {spa_name}. We\'d love to see you again!', align: 'center', fontSize: '16px' }},
      { type: 'image', content: { url: '', alt: 'Welcome Back Image', align: 'center' }},
      { type: 'text', content: { text: 'As a special welcome back offer, enjoy {discount_value} off your next treatment.', align: 'center', fontSize: '16px' }},
      { type: 'button', content: { text: 'Book Now & Save', url: '{booking_link}', align: 'center', backgroundColor: '#3B82F6', textColor: '#ffffff' }},
      { type: 'text', content: { text: 'Use code: {promo_code}', align: 'center', fontSize: '14px', color: '#1E40AF' }}
    ]
  }
];

// Block type definitions
const BLOCK_TYPES = [
  { type: 'header', name: 'Header', icon: Type, description: 'Large heading text' },
  { type: 'text', name: 'Text', icon: Type, description: 'Paragraph text' },
  { type: 'image', name: 'Image', icon: Image, description: 'Add an image' },
  { type: 'button', name: 'Button', icon: Link2, description: 'Call-to-action button' },
  { type: 'divider', name: 'Divider', icon: Minus, description: 'Horizontal line separator' }
];

// Individual Block Component
const EmailBlock = ({ block, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [editing, setEditing] = useState(false);
  const [localContent, setLocalContent] = useState(block.content);

  const handleSave = () => {
    onUpdate(index, { ...block, content: localContent });
    setEditing(false);
  };

  const renderBlockContent = () => {
    switch (block.type) {
      case 'header':
        return (
          <div 
            style={{ 
              textAlign: localContent.align || 'center',
              fontSize: localContent.fontSize || '24px',
              fontWeight: 'bold',
              color: localContent.color || '#1a1a1a',
              backgroundColor: localContent.backgroundColor || 'transparent',
              padding: '16px'
            }}
          >
            {localContent.text || 'Header Text'}
          </div>
        );
      case 'text':
        return (
          <div 
            style={{ 
              textAlign: localContent.align || 'left',
              fontSize: localContent.fontSize || '14px',
              color: localContent.color || '#374151',
              padding: '12px 16px',
              lineHeight: '1.6'
            }}
          >
            {localContent.text || 'Add your text here...'}
          </div>
        );
      case 'image':
        return (
          <div style={{ textAlign: localContent.align || 'center', padding: '16px' }}>
            {localContent.url ? (
              <img src={localContent.url} alt={localContent.alt || ''} style={{ maxWidth: '100%', height: 'auto' }} />
            ) : (
              <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400">
                <Image className="h-12 w-12 mx-auto mb-2" />
                <p>Add image URL</p>
              </div>
            )}
          </div>
        );
      case 'button':
        return (
          <div style={{ textAlign: localContent.align || 'center', padding: '16px' }}>
            <a
              href={localContent.url || '#'}
              style={{
                display: 'inline-block',
                padding: '12px 32px',
                backgroundColor: localContent.backgroundColor || '#7C3AED',
                color: localContent.textColor || '#ffffff',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              {localContent.text || 'Click Here'}
            </a>
          </div>
        );
      case 'divider':
        return (
          <div style={{ padding: '16px' }}>
            <hr style={{ border: 'none', borderTop: '1px solid #E5E7EB' }} />
          </div>
        );
      default:
        return null;
    }
  };

  const renderEditor = () => {
    switch (block.type) {
      case 'header':
      case 'text':
        return (
          <div className="space-y-3 p-4 bg-gray-50 rounded">
            <div>
              <Label>Text</Label>
              {block.type === 'header' ? (
                <Input 
                  value={localContent.text || ''} 
                  onChange={(e) => setLocalContent({...localContent, text: e.target.value})}
                  className="mt-1"
                />
              ) : (
                <Textarea 
                  value={localContent.text || ''} 
                  onChange={(e) => setLocalContent({...localContent, text: e.target.value})}
                  rows={3}
                  className="mt-1"
                />
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Align</Label>
                <select 
                  className="w-full mt-1 px-3 py-2 border rounded"
                  value={localContent.align || 'left'}
                  onChange={(e) => setLocalContent({...localContent, align: e.target.value})}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
              <div>
                <Label>Size</Label>
                <select 
                  className="w-full mt-1 px-3 py-2 border rounded"
                  value={localContent.fontSize || (block.type === 'header' ? '24px' : '14px')}
                  onChange={(e) => setLocalContent({...localContent, fontSize: e.target.value})}
                >
                  <option value="12px">Small</option>
                  <option value="14px">Normal</option>
                  <option value="16px">Medium</option>
                  <option value="18px">Large</option>
                  <option value="24px">XL</option>
                  <option value="32px">XXL</option>
                </select>
              </div>
              <div>
                <Label>Color</Label>
                <Input 
                  type="color"
                  value={localContent.color || '#374151'}
                  onChange={(e) => setLocalContent({...localContent, color: e.target.value})}
                  className="mt-1 h-10"
                />
              </div>
            </div>
            {block.type === 'header' && (
              <div>
                <Label>Background Color</Label>
                <Input 
                  type="color"
                  value={localContent.backgroundColor || '#ffffff'}
                  onChange={(e) => setLocalContent({...localContent, backgroundColor: e.target.value})}
                  className="mt-1 h-10 w-20"
                />
              </div>
            )}
          </div>
        );
      case 'image':
        return (
          <div className="space-y-3 p-4 bg-gray-50 rounded">
            <div>
              <Label>Image URL</Label>
              <Input 
                value={localContent.url || ''} 
                onChange={(e) => setLocalContent({...localContent, url: e.target.value})}
                placeholder="https://example.com/image.jpg"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Alt Text</Label>
              <Input 
                value={localContent.alt || ''} 
                onChange={(e) => setLocalContent({...localContent, alt: e.target.value})}
                placeholder="Image description"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Align</Label>
              <select 
                className="w-full mt-1 px-3 py-2 border rounded"
                value={localContent.align || 'center'}
                onChange={(e) => setLocalContent({...localContent, align: e.target.value})}
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        );
      case 'button':
        return (
          <div className="space-y-3 p-4 bg-gray-50 rounded">
            <div>
              <Label>Button Text</Label>
              <Input 
                value={localContent.text || ''} 
                onChange={(e) => setLocalContent({...localContent, text: e.target.value})}
                placeholder="Click Here"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Link URL</Label>
              <Input 
                value={localContent.url || ''} 
                onChange={(e) => setLocalContent({...localContent, url: e.target.value})}
                placeholder="https://example.com"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Align</Label>
                <select 
                  className="w-full mt-1 px-3 py-2 border rounded"
                  value={localContent.align || 'center'}
                  onChange={(e) => setLocalContent({...localContent, align: e.target.value})}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
              <div>
                <Label>BG Color</Label>
                <Input 
                  type="color"
                  value={localContent.backgroundColor || '#7C3AED'}
                  onChange={(e) => setLocalContent({...localContent, backgroundColor: e.target.value})}
                  className="mt-1 h-10"
                />
              </div>
              <div>
                <Label>Text Color</Label>
                <Input 
                  type="color"
                  value={localContent.textColor || '#ffffff'}
                  onChange={(e) => setLocalContent({...localContent, textColor: e.target.value})}
                  className="mt-1 h-10"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="group relative border rounded-lg bg-white hover:border-primary/50 transition-colors">
      {/* Drag handle and controls */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gray-50 rounded-l-lg flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
      </div>
      
      {/* Block controls */}
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="sm" onClick={() => onMoveUp(index)} disabled={isFirst}>
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onMoveDown(index)} disabled={isLast}>
          <ChevronDown className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)}>
          <Edit2 className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(index)} className="text-red-500 hover:text-red-700">
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Block content */}
      <div className="ml-8" onClick={() => !editing && setEditing(true)}>
        {renderBlockContent()}
      </div>

      {/* Editor panel */}
      {editing && (
        <div className="border-t">
          {renderEditor()}
          <div className="flex justify-end gap-2 p-3 bg-gray-50">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave}>Apply</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminEmailTemplates = () => {
  const { toast } = useToast();
  const { branding } = useBranding();
  
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [saving, setSaving] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedTemplates();
  }, []);

  const fetchSavedTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/email-templates/marketing?practiceId=demo-practice-001`);
      if (response.ok) {
        const data = await response.json();
        setSavedTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLibraryTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateSubject(`${template.name} - {spa_name}`);
    setBlocks([...template.blocks]);
    setActiveTab('editor');
  };

  const handleAddBlock = (type) => {
    const newBlock = {
      type,
      content: getDefaultContent(type)
    };
    setBlocks([...blocks, newBlock]);
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'header':
        return { text: 'Header Text', align: 'center', fontSize: '24px', color: '#1a1a1a' };
      case 'text':
        return { text: 'Add your text here...', align: 'left', fontSize: '14px', color: '#374151' };
      case 'image':
        return { url: '', alt: 'Image', align: 'center' };
      case 'button':
        return { text: 'Click Here', url: '#', align: 'center', backgroundColor: '#7C3AED', textColor: '#ffffff' };
      case 'divider':
        return {};
      default:
        return {};
    }
  };

  const handleUpdateBlock = (index, updatedBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = updatedBlock;
    setBlocks(newBlocks);
  };

  const handleDeleteBlock = (index) => {
    setBlocks(blocks.filter((_, i) => i !== index));
  };

  const handleMoveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= blocks.length) return;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    setBlocks(newBlocks);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: 'Error', description: 'Please enter a template name', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const templateData = {
        practiceId: branding?.practiceId || 'demo-practice-001',
        name: templateName,
        subject: templateSubject,
        category: selectedTemplate?.category || 'custom',
        blocks: blocks,
        libraryTemplateId: selectedTemplate?.id || null
      };

      const response = await fetch(`${API}/email-templates/marketing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      });

      if (!response.ok) throw new Error('Failed to save');
      
      toast({ title: 'Template Saved!', description: `${templateName} has been saved.` });
      fetchSavedTemplates();
      setActiveTab('saved');
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSavedTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateSubject(template.subject);
    setBlocks(template.blocks || []);
    setActiveTab('editor');
  };

  const handleDeleteSavedTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${API}/email-templates/marketing/${templateId}`, { method: 'DELETE' });
      toast({ title: 'Template deleted' });
      fetchSavedTemplates();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const renderEmailPreview = () => {
    const width = previewMode === 'mobile' ? '375px' : '600px';
    return (
      <div 
        className="mx-auto bg-white shadow-lg rounded-lg overflow-hidden transition-all"
        style={{ width, maxWidth: '100%' }}
      >
        <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 border-b">
          <strong>Subject:</strong> {templateSubject.replace('{spa_name}', branding?.practiceName || 'Your Spa')}
        </div>
        <div className="p-4">
          {blocks.map((block, idx) => (
            <div key={idx}>
              {block.type === 'header' && (
                <div style={{
                  textAlign: block.content.align,
                  fontSize: block.content.fontSize,
                  fontWeight: 'bold',
                  color: block.content.color,
                  backgroundColor: block.content.backgroundColor || 'transparent',
                  padding: '16px'
                }}>
                  {block.content.text}
                </div>
              )}
              {block.type === 'text' && (
                <div style={{
                  textAlign: block.content.align,
                  fontSize: block.content.fontSize,
                  color: block.content.color,
                  padding: '12px 16px',
                  lineHeight: '1.6'
                }}>
                  {block.content.text}
                </div>
              )}
              {block.type === 'image' && block.content.url && (
                <div style={{ textAlign: block.content.align, padding: '16px' }}>
                  <img src={block.content.url} alt={block.content.alt} style={{ maxWidth: '100%' }} />
                </div>
              )}
              {block.type === 'button' && (
                <div style={{ textAlign: block.content.align, padding: '16px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '12px 32px',
                    backgroundColor: block.content.backgroundColor,
                    color: block.content.textColor,
                    borderRadius: '6px',
                    fontWeight: '600'
                  }}>
                    {block.content.text}
                  </span>
                </div>
              )}
              {block.type === 'divider' && (
                <hr style={{ margin: '16px', border: 'none', borderTop: '1px solid #E5E7EB' }} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-email-templates">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-6 w-6 text-blue-500" />
            Email Templates
          </h1>
          <p className="text-gray-600">Create beautiful marketing emails with our drag-and-drop builder</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4" />
            Template Library
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            My Templates
          </TabsTrigger>
          <TabsTrigger value="editor" className="flex items-center gap-2" disabled={blocks.length === 0}>
            <Sparkles className="h-4 w-4" />
            Editor
          </TabsTrigger>
        </TabsList>

        {/* Template Library */}
        <TabsContent value="templates" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {EMAIL_TEMPLATE_LIBRARY.map((template) => (
              <Card 
                key={template.id} 
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                onClick={() => handleSelectLibraryTemplate(template)}
                data-testid={`template-${template.id}`}
              >
                <div 
                  className="h-32 rounded-t-lg"
                  style={{ background: template.thumbnail }}
                />
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <Badge variant="secondary">{template.category}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500">{template.description}</p>
                  <Button className="w-full mt-4" variant="outline">
                    <Edit2 className="h-4 w-4 mr-2" />
                    Customize
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Start from scratch */}
          <Card className="border-dashed border-2 hover:border-primary/50 transition-colors cursor-pointer" 
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateName('New Template');
                  setTemplateSubject('New Email - {spa_name}');
                  setBlocks([]);
                  setActiveTab('editor');
                }}>
            <CardContent className="py-12 text-center">
              <Plus className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Start from Scratch</h3>
              <p className="text-gray-500">Create a custom template with our block editor</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Saved Templates */}
        <TabsContent value="saved" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : savedTemplates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No saved templates yet</h3>
                <p className="text-gray-500 mb-4">Start by customizing a template from the library</p>
                <Button onClick={() => setActiveTab('templates')}>
                  Browse Templates
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedTemplates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <Badge>{template.category}</Badge>
                    </div>
                    <CardDescription className="truncate">{template.subject}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-400 mb-4">
                      {template.blocks?.length || 0} blocks
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEditSavedTemplate(template)}>
                        <Edit2 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteSavedTemplate(template.id)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Editor */}
        <TabsContent value="editor" className="space-y-6">
          {/* Back button */}
          <Button variant="ghost" onClick={() => setActiveTab('templates')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: Block Editor */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Template Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input 
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="My Email Template"
                      className="mt-1"
                      data-testid="template-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email Subject</Label>
                    <Input 
                      value={templateSubject}
                      onChange={(e) => setTemplateSubject(e.target.value)}
                      placeholder="Your subject line here"
                      className="mt-1"
                      data-testid="template-subject-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use {'{spa_name}'}, {'{client_name}'} for personalization</p>
                  </div>
                </CardContent>
              </Card>

              {/* Add Block */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Add Block</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {BLOCK_TYPES.map((blockType) => {
                      const IconComponent = blockType.icon;
                      return (
                        <Button
                          key={blockType.type}
                          variant="outline"
                          className="flex flex-col h-auto py-3"
                          onClick={() => handleAddBlock(blockType.type)}
                          data-testid={`add-${blockType.type}-btn`}
                        >
                          <IconComponent className="h-5 w-5 mb-1" />
                          <span className="text-xs">{blockType.name}</span>
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Blocks List */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Email Content ({blocks.length} blocks)</CardTitle>
                </CardHeader>
                <CardContent>
                  {blocks.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Type className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Add blocks to build your email</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {blocks.map((block, idx) => (
                        <EmailBlock
                          key={idx}
                          block={block}
                          index={idx}
                          onUpdate={handleUpdateBlock}
                          onDelete={handleDeleteBlock}
                          onMoveUp={() => handleMoveBlock(idx, 'up')}
                          onMoveDown={() => handleMoveBlock(idx, 'down')}
                          isFirst={idx === 0}
                          isLast={idx === blocks.length - 1}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right: Preview */}
            <div className="space-y-4">
              <Card className="sticky top-4">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">Preview</CardTitle>
                    <div className="flex gap-2">
                      <Button 
                        variant={previewMode === 'desktop' ? 'default' : 'outline'} 
                        size="sm"
                        onClick={() => setPreviewMode('desktop')}
                      >
                        <Monitor className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant={previewMode === 'mobile' ? 'default' : 'outline'} 
                        size="sm"
                        onClick={() => setPreviewMode('mobile')}
                      >
                        <Smartphone className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="bg-gray-100 rounded-b-lg py-6">
                  {renderEmailPreview()}
                </CardContent>
              </Card>

              {/* Save Button */}
              <Button 
                className="w-full" 
                size="lg" 
                onClick={handleSaveTemplate}
                disabled={saving || blocks.length === 0}
                data-testid="save-template-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Help Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Pro Tips</p>
            <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
              <li>Use placeholders like {'{client_name}'}, {'{spa_name}'}, {'{discount_value}'} for personalization</li>
              <li>Click any block to edit its content</li>
              <li>Drag blocks to reorder them</li>
              <li>Preview on both desktop and mobile before sending</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailTemplates;
